import React, { useContext } from "react";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";

import { Spinner } from "../../../components/ui";
import { Search, SwitchVertical } from "../../../components/icons/svg/outline";
import DispatchFiltersPanel from "./DispatchFiltersPanel";
import DispatchSwitcher from "./DispatchSwitcher";

import { formatShortMonthDay } from "../../../utils/datetime/core";

/**
 * Dispatch control bar: date range, search, filters, and week nav.
 * Layout and chrome match Schedule {@link TimelineHeader} + {@link ScheduleToolbar}.
 * Title / last-modified live above this bar (see DailyDispatch).
 *
 * @returns {JSX.Element}
 */
const TopBar = () => {
  const {
    dateRange,
    reloadJobs,
    showWeekends,
    setShowWeekends,
  } = useContext(WeeklyContext);
  const { isReadOnly, isLoading, compact } = useContext(DispatchContext);

  const [rangeStart, rangeEnd] = dateRange;
  const dateRangeString = `${formatShortMonthDay(rangeStart)} - ${formatShortMonthDay(rangeEnd)}`;

  return (
    <div className="flex h-[38px] min-w-0 items-center gap-2 border-b border-gray-300 bg-gray-100 px-4">
      <div className="shrink-0 text-sm font-semibold text-gray-800">
        {dateRangeString}
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
            <Search className="h-4 w-4 text-gray-400" aria-hidden />
          </div>
          <input
            type="text"
            disabled
            placeholder="Search projects, groups, or scopes..."
            className="block w-full rounded-md border border-gray-300 bg-white py-0.5 pl-8 pr-2 text-sm leading-5 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
            aria-label="Search projects, groups, or scopes"
          />
        </div>
        {!compact && (
          <>
            <DispatchFiltersPanel />
            {!isReadOnly && (
              <button
                type="button"
                aria-label="Update sort"
                title="Update sort"
                className="inline-flex h-6 items-center rounded-md border border-gray-300 bg-white px-2 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-900 focus:ring-offset-1"
                onClick={reloadJobs}
              >
                <SwitchVertical className="h-4 w-4" aria-hidden />
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        <div className="min-w-[20px]">
          {isLoading && (
            <Spinner
              variant="oval"
              size="h-5 w-5"
              color="#4fa94d"
              secondaryColor="#4fa94d"
              strokeWidth={4}
              strokeWidthSecondary={4}
            />
          )}
        </div>
        {compact ? (
          <label className="inline-flex h-6 items-center gap-1.5 rounded border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 hover:bg-gray-200">
            <input
              type="checkbox"
              className="h-3 w-3 rounded border-gray-300 text-primary focus:ring-primary"
              checked={showWeekends}
              onChange={(event) => setShowWeekends(event.target.checked)}
            />
            Show Weekends
          </label>
        ) : null}
        <DispatchSwitcher />
      </div>
    </div>
  );
};

export default TopBar;
